import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Ship, CalendarClock, AlertTriangle, ClipboardList, Anchor, Plus, Loader2, RefreshCw, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import OpsAuthorizations from "@/components/OpsAuthorizations";

const BUCKETS = [
  { k: "today", label: "Today" },
  { k: "upcoming", label: "Upcoming" },
  { k: "overdue", label: "Overdue" },
  { k: "completed", label: "Completed" },
];

export default function OpsDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bucket, setBucket] = useState("today");
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: v }] = await Promise.all([
        api.get("/ops/dashboard"),
        api.get("/ops/visits", { params: { bucket } }),
      ]);
      setStats(s);
      setVisits(v);
    } catch (e) {
      toast.error("Could not load operations data.");
    } finally {
      setLoading(false);
    }
  }, [bucket]);

  useEffect(() => { load(); }, [load]);

  const onLogout = async () => { await logout(); navigate("/admin/login"); };

  const startInspection = async (visitId) => {
    try {
      const { data } = await api.post(`/ops/visits/${visitId}/inspection`);
      navigate(`/ops/inspect/${visitId}`, { state: { inspectionId: data.id } });
    } catch (e) {
      toast.error("Could not open inspection.");
    }
  };

  const CARDS = stats ? [
    { label: "Active Vessels", value: stats.active_vessels, icon: Ship },
    { label: "Today's Visits", value: stats.today_visits, icon: CalendarClock },
    { label: "Overdue", value: stats.overdue, icon: AlertTriangle },
    { label: "Awaiting Completion", value: stats.awaiting_completion, icon: ClipboardList },
    { label: "Urgent Issues", value: stats.urgent_issues, icon: AlertTriangle },
    { label: "Leads to Follow-up", value: stats.leads_followup, icon: Anchor },
  ] : [];

  return (
    <div className="min-h-screen bg-ivory dark:bg-navy-900" data-testid="ops-dashboard">
      <Seo title="Operations — Hussar Marine Concierge" description="HMC operations dashboard." />
      <header className="bg-navy-900 border-b border-gold-500/15 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark className="h-11 w-11" />
            <div>
              <p className="font-serif text-lg text-white leading-tight">HMC Operations</p>
              <p className="eyebrow text-gold-400 -mt-0.5">Fleet & Visit Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/15 text-slate-200 hover:text-gold-400" data-testid="ops-refresh" aria-label="Refresh"><RefreshCw className="h-4 w-4" /></button>
            <span className="hidden sm:block text-sm text-slate-300">{user?.email}</span>
            <button onClick={onLogout} className="inline-flex items-center gap-2 border border-white/15 text-slate-200 hover:text-gold-400 text-sm px-4 py-2 rounded-full" data-testid="ops-logout"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {CARDS.map((c) => (
            <div key={c.label} className="bg-white dark:bg-navy-800 border border-gold-500/20 rounded-2xl p-4" data-testid={`ops-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <c.icon className="h-5 w-5 text-gold-500" />
              <p className="mt-3 text-2xl font-bold text-navy-900 dark:text-white">{c.value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {BUCKETS.map((b) => (
              <button key={b.k} onClick={() => setBucket(b.k)} data-testid={`ops-bucket-${b.k}`}
                className={`px-4 py-2 rounded-full text-sm font-medium ${bucket === b.k ? "bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900" : "bg-white dark:bg-navy-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10"}`}>
                {b.label}
              </button>
            ))}
          </div>
          <button onClick={() => setShowSetup((s) => !s)} className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 text-sm font-semibold px-4 py-2 rounded-full" data-testid="ops-new-vessel-toggle"><Plus className="h-4 w-4" /> New Vessel</button>
        </div>

        {showSetup && <SetupPanel onDone={() => { setShowSetup(false); load(); }} />}

        <div className="mt-6 space-y-3" data-testid="ops-visits-list">
          {loading ? (
            <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : visits.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-navy-800 rounded-2xl border border-dashed border-slate-300 dark:border-white/10 text-slate-500" data-testid="ops-visits-empty">No {bucket} visits.</div>
          ) : visits.map((v) => (
            <div key={v.id} className="bg-white dark:bg-navy-800 border border-gold-500/15 rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`visit-row-${v.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Ship className="h-4 w-4 text-gold-500" />
                  <span className="font-serif text-lg text-navy-900 dark:text-white">{v.vessel_name || "Vessel"}</span>
                  <span className={`eyebrow px-2 py-0.5 rounded-full border text-[10px] ${v.state === "overdue" ? "border-red-400 text-red-500" : v.state === "completed" ? "border-green-500/40 text-green-600" : "border-gold-500/40 text-gold-600"}`}>{v.state}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{v.scheduled_date} · {v.marina || "—"} {v.slip ? `· Slip ${v.slip}` : ""}</p>
              </div>
              {v.state !== "completed" && (
                <button onClick={() => startInspection(v.id)} className="inline-flex items-center gap-1.5 bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900 text-sm font-semibold px-4 py-2.5 rounded-full shrink-0" data-testid={`start-inspection-${v.id}`}>
                  Inspect <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <OpsAuthorizations />
      </div>
    </div>
  );
}

const SetupPanel = ({ onDone }) => {
  const [f, setF] = useState({ cname: "", cemail: "", vname: "", marina: "", slip: "", tier: "Premier Vigilance", cadence: "weekly", start: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));
  const submit = async () => {
    if (!f.cname || !f.cemail || !f.vname) { toast.error("Customer name, email and vessel name are required."); return; }
    setBusy(true);
    try {
      const { data: c } = await api.post("/ops/customers", { name: f.cname, email: f.cemail });
      const { data: v } = await api.post("/ops/vessels", { customer_id: c.id, name: f.vname, marina: f.marina, slip: f.slip, service_tier: f.tier });
      await api.put(`/ops/vessels/${v.id}/plan`, { cadence: f.cadence, start_date: f.start, active: true });
      toast.success("Vessel added and schedule created.");
      onDone();
    } catch (e) {
      toast.error("Could not create vessel.");
    } finally { setBusy(false); }
  };
  const inp = "w-full bg-ivory dark:bg-navy-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-white";
  return (
    <div className="mt-5 bg-white dark:bg-navy-800 border border-gold-500/20 rounded-2xl p-6" data-testid="ops-setup-panel">
      <p className="font-serif text-lg text-navy-900 dark:text-white mb-4">Add a vessel & service plan</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <input className={inp} placeholder="Customer name" value={f.cname} onChange={set("cname")} data-testid="setup-cname" />
        <input className={inp} placeholder="Customer email" value={f.cemail} onChange={set("cemail")} data-testid="setup-cemail" />
        <input className={inp} placeholder="Vessel name" value={f.vname} onChange={set("vname")} data-testid="setup-vname" />
        <input className={inp} placeholder="Marina" value={f.marina} onChange={set("marina")} data-testid="setup-marina" />
        <input className={inp} placeholder="Slip" value={f.slip} onChange={set("slip")} data-testid="setup-slip" />
        <select className={inp} value={f.cadence} onChange={set("cadence")} data-testid="setup-cadence">
          <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option>
        </select>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <input type="date" className={inp + " max-w-[180px]"} value={f.start} onChange={set("start")} data-testid="setup-start" />
        <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-navy-900 text-sm font-semibold px-5 py-2.5 rounded-full" data-testid="setup-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
        </button>
      </div>
    </div>
  );
};
